import React from "react";
import { Box, Select, MenuItem, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import { parseMfbParts } from "../../utils/mfbUtils";

const MFBActivity = ({
  currentActivity,
  submitted, // submitted[currentActivity.ContentId]
  mfbSelections,
  handleOptionChange,
  preview = false, // 🔥 NEW: marks Authoring preview
  shuffledMfbOptions,
}) => {
  const parts = preview
    ? parseMfbParts(currentActivity?.Options?.[0]?.Value || "")
    : shuffledMfbOptions || [];

  // USER selections
  const userSelections = Object.values(
    mfbSelections?.[currentActivity.ContentId] || {}
  ).map((v) => (typeof v === "string" ? v.trim() : v));

  // Correct answers (just for student mode)
  const correctAnswers = parseMfbParts(
    currentActivity?.Options?.[0]?.Value || ""
  )
    .filter((p) => Array.isArray(p))
    .map((opts) => opts[0].trim());

  const isCorrect =
    !preview &&
    submitted &&
    userSelections.length === correctAnswers.length &&
    userSelections.every((sel, i) => sel === correctAnswers[i]);

  const resultBgColor = submitted
    ? isCorrect
      ? "#e8f5e9"
      : "#ffebee"
    : "transparent";

  // Rationales only for STUDENT mode
  const rationaleItems = !preview
    ? currentActivity.Options.map((opt) => ({
        txt: opt.Rationale?.Text?.trim(),
        cid: opt.Rationale?.ContentId,
      }))
    : [];

  return (
    <>
      <Box
        dir="rtl"
        sx={{
          textAlign: "right",
          fontSize: "1.5rem",
          fontFamily: "Traditional Arabic, serif",
          mt: 2,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {parts.map((part, index) =>
            Array.isArray(part) ? (
              <Select
                key={index}
                disabled={!preview && submitted}
                {...(preview
                  ? { defaultValue: "" } // PREVIEW MODE → UNCONTROLLED
                  : {
                      value:
                        mfbSelections[currentActivity.ContentId]?.[index] || "", // STUDENT → CONTROLLED
                    })}
                onChange={(e) => {
                  if (!preview) {
                    handleOptionChange(
                      currentActivity.ContentId,
                      index,
                      e.target.value
                    );
                  }
                }}
                sx={{
                  minWidth: 120,
                  fontSize: "1.5rem",
                  "& .MuiSelect-select": { p: "2px" },
                }}
              >
                {part.map((opt, i) => (
                  <MenuItem key={i} value={opt} sx={{ fontSize: "1.5rem" }}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Box
                key={index}
                component="span"
                sx={{ fontSize: "1.5rem", whiteSpace: "pre-wrap" }}
              >
                {part}
              </Box>
            )
          )}
        </Box>
      </Box>
      {!preview && submitted && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            backgroundColor: resultBgColor,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            {isCorrect ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "green" }} />
            ) : (
              <CancelIcon sx={{ fontSize: 40, color: "red" }} />
            )}
          </Box>

          <Typography
            sx={{
              fontWeight: "bold",
              textAlign: "center",
              color: isCorrect ? "green" : "red",
              mb: 2,
            }}
          >
            {isCorrect ? "Correct Answer!" : "Incorrect Answer!"}
          </Typography>

          {/* Rationales */}
          <Box>
            {rationaleItems.map((r, idx) => (
              <Box key={idx} sx={{ p: 1, my: 1, borderRadius: 2 }}>
                {r.txt ? (
                  <Typography>Rationale: {r.txt}</Typography>
                ) : r.cid ? (
                  <Typography>Rationale ID: {r.cid}</Typography>
                ) : (
                  <Typography fontStyle="italic">
                    No rationale provided.
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </>
  );
};

export default MFBActivity;
